import React, { useState } from 'react';

const BiologyCalculators = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const quickAccessLinks = [
    { name: 'Hardy-Weinberg Calculator', path: '/calculator/hardy-weinberg', category: 'Genetics' },
    { name: 'Protein Mass Calculator', path: '/calculator/protein-mass', category: 'Genetics' },
    { name: 'DNA Mass Calculator', path: '/calculator/dna-mass', category: 'Genetics' },
    { name: 'Cell Density Calculator', path: '/calculator/cell-density', category: 'Cell Biology' },
    { name: 'Cell Population Doubling Time', path: '/calculator/doubling-time-cells', category: 'Cell Biology' },
    { name: 'Dilution Calculator', path: '/calculator/dilution-factor', category: 'Cell Biology' }
  ];

  const handleReadMore = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header Section */}
      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🧬</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Biology Calculators</h1>
            <p className="calculator-count text-xl text-blue-600 font-semibold">150+ calculators</p>
          </div>
        </div>
      </div>

      {/* Description Section */}
      <div className="calculator-description space-y-6 mb-12">
        <p className="text-gray-700 text-lg">
          Explore our comprehensive collection of biology calculators designed to help you solve complex biological calculations. From genetics to ecology, our tools simplify calculations for students, researchers, and professionals in the field of biology.
        </p>
        
        <ul className="list-none marker:text-blue-600 pl-8 space-y-3">
          {['Accelerating research by automating complex calculations', 'Ensuring accuracy in laboratory experiments', 'Supporting educational understanding of biological concepts', 'Streamlining data analysis in research projects'].map((item, index) => (
            <li key={index} className="flex items-start">
              <span className="flex-shrink-0 h-6 w-6 text-blue-600 mr-3">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                </svg>
              </span>
              <span className="text-gray-700">{item}</span>
            </li>
          ))}
        </ul>

        {/* Collapsible Content */}
        <div className="relative max-h-[${isExpanded ? 'none' : '200px'}]" style={{ transition: 'max-height 0.3s ease' }}>
          <div className="space-y-6 pb-6">
            <h3 className="text-xl font-semibold text-gray-900">Why Choose Our Biology Calculators?</h3>
            <ul className="space-y-4">
              {['Precision & Accuracy', 'Educational Support', 'Time Efficiency', 'Laboratory Applications'].map((item, index) => (
                <li key={index} className="space-y-2">
                  <h4 className="flex items-center gap-2 text-lg font-semibold text-gray-700">
                    <span className="h-6 w-6 text-blue-600">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </span>
                    {item}
                  </h4>
                  <p className="text-gray-600 pl-8">
                    {[
                      'Our calculators use validated algorithms and formulas to ensure reliable results for your research and experiments.',
                      'Perfect for students learning complex biological concepts, with step-by-step explanations and visual aids.',
                      'Automate time-consuming calculations, allowing you to focus on analysis and interpretation of results.',
                      'Essential tools for lab work, from solution preparation to data analysis.'
                    ][index]}
                  </p>
                </li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-900">Features & Capabilities</h3>
            <ul className="space-y-4">
              {['Advanced Visualization', 'Result History', 'Mobile Responsive', 'Regular Updates'].map((item, index) => (
                <li key={index} className="space-y-2">
                  <h4 className="flex items-center gap-2 text-lg font-semibold text-gray-700">
                    <span className="h-6 w-6 text-blue-600">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </span>
                    {item}
                  </h4>
                  <p className="text-gray-600 pl-8">
                    {[
                      'Interactive charts and graphs for better data interpretation.',
                      'Save and export your calculations for future reference.',
                      'Access our calculators anywhere, from any device.',
                      'Continuously updated with the latest scientific standards and methods.'
                    ][index]}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Read More Button */}
        <button 
          onClick={handleReadMore}
          className="inline-flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-md shadow-sm hover:bg-blue-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all"
        >
          {isExpanded ? 'Show Less' : 'Read More'}
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 100 4 2 2 0 000-4zm0 8a2 2 0 100 4 2 2 0 000-4zm-4-2a2 2 0 114 0 2 2 0 01-4 0zm4 0a2 2 0 114 0 2 2 0 01-4 0z" />
          </svg>
        </button>
      </div>

      {/* Calculator Sections */}
      {[
        {
          title: 'Genetics calculators',
          emoji: '🧬',
          summary: 'Our genetics calculators are indispensable tools for population genetics studies and molecular biology research. They help you:',
          items: [
            'Analyze population genetics data using Hardy-Weinberg principles',
            'Calculate molecular weights for DNA and protein analysis',
            'Support genetic research and laboratory experiments',
            'Facilitate molecular biology calculations for research papers'
          ],
          calculators: [
            { name: 'Hardy-Weinberg Calculator', path: '/calculator/hardy-weinberg', description: 'Calculate allele and genotype frequencies in populations' },
            { name: 'Protein Mass Calculator', path: '/calculator/protein-mass', description: 'Calculate molecular mass of proteins from amino acid sequences' },
            { name: 'DNA Mass Calculator', path: '/calculator/dna-mass', description: 'Calculate the mass of DNA sequences' },
            { name: 'Allele Frequency Calculator', path: '/calculator/allele-frequency', description: 'Calculate allele frequencies in populations' },
            { name: 'Punnett Square Calculator', path: '/calculator/punnett-square', description: 'Calculate Punnett square for genetic crosses' },
            { name: 'qPCR Efficiency Calculator', path: '/calculator/qpcr-efficiency', description: 'Calculate qPCR efficiency for gene expression analysis' },
            { name: 'Dihybrid Cross Calculator - Punnett Square', path: '/calculator/dihybrid-cross', description: 'Calculate Punnett square for dihybrid crosses' },
            { name: 'DNA Copy Number Calculator', path: '/calculator/dna-copy-number', description: 'Calculate DNA copy number for gene expression analysis' },
            { name: 'Trihybrid Cross Calculator - Punnett Square', path: '/calculator/trihybrid-cross', description: 'Calculate Punnett square for trihybrid crosses' }
          ]
        },
        {
          title: 'Cell Biology calculators',
          emoji: '🦠',
          summary: 'Cell biology calculators are crucial for cell culture work and cellular analysis. These tools enable you to:',
          items: [
            'Optimize cell culture conditions with precise calculations',
            'Monitor cell growth and population dynamics',
            'Prepare accurate media dilutions for experiments',
            'Track cell proliferation rates and viability'
          ],
          calculators: [
            { name: 'Cell Density Calculator', path: '/calculator/cell-density', description: 'Calculate cell concentration in culture' },
            { name: 'Cell Population Doubling Time', path: '/calculator/doubling-time-cells', description: 'Calculate cell population growth rates' },
            { name: 'Dilution Calculator', path: '/calculator/dilution-factor', description: 'Calculate dilutions for cell culture media' },
            { name: 'Cell Doubling Time Calculator', path: '/calculator/cell-doubling-time', description: 'Calculate cell doubling time for cell culture' },
            { name: 'DNA Concentration Calculator', path: '/calculator/dna-concentration', description: 'Calculate DNA concentration in solutions' },
            { name: 'Ligation Calculator', path: '/calculator/ligation', description: 'Calculate ligation efficiency for molecular biology experiments' }
          ]
        },
        {
          title: 'Animal Pregnancy Calculators',
          emoji: '🐱',
          summary: 'Our animal pregnancy calculators are essential tools for veterinarians, farmers, and pet owners. These calculators help you:',
          items: [
            'Predict due dates for various animal species',
            'Track gestation periods for farm animals',
            'Plan breeding schedules accurately',
            'Monitor pregnancy progress in pets'
          ],
          calculators: [
            { name: 'Cat Pregnancy Calculator', path: '/calculator/cat-pregnancy', description: 'Calculate cat gestation period and due date' },
            { name: 'Cow Gestation Calculator', path: '/calculator/cow-gestation', description: 'Calculate cattle pregnancy duration' },
            { name: 'Dog Pregnancy Calculator', path: '/calculator/dog-pregnancy', description: 'Calculate canine gestation period' },
            { name: 'Goat Gestation Calculator', path: '/calculator/goat-gestation', description: 'Calculate goat pregnancy duration' },
            { name: 'Guinea Pig Pregnancy Calculator', path: '/calculator/guinea-pig-pregnancy', description: 'Calculate guinea pig gestation period' },
            { name: 'Mare Gestation Calculator', path: '/calculator/mare-gestation', description: 'Calculate horse pregnancy duration' },
            { name: 'Llama Calculator', path: '/calculator/llama', description: 'Calculate llama gestation period' },
            { name: 'Rabbit Gestation Calculator', path: '/calculator/rabbit-gestation', description: 'Calculate rabbit pregnancy duration' },
            { name: 'Sheep Gestation Calculator', path: '/calculator/sheep-gestation', description: 'Calculate sheep pregnancy duration' },
            { name: 'Swine Gestation Calculator', path: '/calculator/swine-gestation', description: 'Calculate pig pregnancy duration' }
          ]
        },
        {
          title: 'Dog Calculators',
          emoji: '🐶',
          summary: 'Our dog calculators are essential tools for pet owners, veterinarians, and dog breeders. These calculators help you:',
          items: [
            'Manage your dog\'s health and nutrition',
            'Calculate accurate medication dosages',
            'Plan for dog ownership costs and care',
            'Monitor your dog\'s growth and development'
          ],
          calculators: [
            { name: 'Benadryl Dosage Calculator for Dogs', path: '/calculator/dog-benadryl', description: 'Calculate safe dosage of Benadryl for your dog' },
            { name: 'Cephalexin Dosage Calculator for Dogs', path: '/calculator/dog-cephalexin', description: 'Calculate Cephalexin dosage for your dog' },
            { name: 'Cost of Owning a Dog Calculator', path: '/calculator/dog-cost', description: 'Estimate the total cost of dog ownership' },
            { name: 'Dog Age Calculator', path: '/calculator/dog-age', description: 'Convert dog years to human years' },
            { name: 'Dog BMI Calculator', path: '/calculator/dog-bmi', description: 'Calculate your dog\'s body mass index' },
            { name: 'Dog Nutrition Calculator', path: '/calculator/dog-nutrition', description: 'Calculate your dog\'s daily nutritional needs' },
            { name: 'Dog Chocolate Toxicity Calculator', path: '/calculator/dog-chocolate-toxicity', description: 'Calculate chocolate toxicity risk for dogs' },
            { name: 'Dog Crate Size Calculator', path: '/calculator/dog-crate-size', description: 'Find the right crate size for your dog' },
            { name: 'Dog Food Calculator', path: '/calculator/dog-food', description: 'Calculate your dog\'s daily food requirements' },
            { name: 'Dog Harness Size Calculator', path: '/calculator/dog-harness-size', description: 'Find the perfect harness size for your dog' },
            { name: 'Dog Heat Cycle Calculator', path: '/calculator/dog-heat-cycle', description: 'Track your dog\'s heat cycle and breeding window' },
            { name: 'Dog Life Expectancy Calculator', path: '/calculator/dog-life-expectancy', description: 'Estimate your dog\'s life expectancy' },
            { name: 'Metacam Dosage Calculator for Dogs', path: '/calculator/dog-metacam-dosage', description: 'Calculate Metacam dosage for your dog' },
            { name: 'Dog Onion Toxicity Calculator', path: '/calculator/dog-onion-toxicity', description: 'Calculate onion toxicity risk for dogs' },
            { name: 'Dog Quality of Life Calculator', path: '/calculator/dog-quality-of-life', description: 'Assess your dog\'s quality of life' },
            { name: 'Dog Raisin Toxicity Calculator', path: '/calculator/dog-raisin-toxicity', description: 'Calculate raisin toxicity risk for dogs' },
            { name: 'Dog Size Calculator', path: '/calculator/dog-size', description: 'Estimate your dog\'s adult size' },
            { name: 'Dog Water Intake Calculator', path: '/calculator/dog-water-intake', description: 'Calculate your dog\'s daily water needs' },
            { name: 'Dog Omega-3 Calculator', path: '/calculator/dog-omega3', description: 'Calculate optimal Omega-3 dosage for dogs' },
            { name: 'Tramadol Dosage Calculator for Dogs', path: '/calculator/dog-tramadol', description: 'Calculate Tramadol dosage for your dog' }
          ]
        }
      ].map((section, index) => (
        <div key={index} className="calculator-section mb-16">
          {/* Section Header */}
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              {section.title} <span className="emoji text-2xl">{section.emoji}</span>
            </h2>
          </div>

          {/* Section Summary */}
          <div className="bg-blue-50 p-6 rounded-lg mb-8">
            <p className="text-gray-700">{section.summary}</p>
            <ul className="mt-4 space-y-2 pl-8 list-disc">
              {section.items.map((item, i) => (
                <li key={i} className="text-gray-600">{item}</li>
              ))}
            </ul>
          </div>

          {/* Calculator Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {section.calculators.map((calculator, i) => (
              <div key={i} className="calculator-card bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200">
                <a href={calculator.path} className="block calculator-link p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 hover:text-blue-600 transition-colors">{calculator.name}</h3>
                  <p className="text-gray-600 text-sm">{calculator.description}</p>
                </a>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BiologyCalculators;